import React, { ReactNode, useState } from 'react';
import { Form, Button, Table, Alert, Card, Badge, Accordion } from 'react-bootstrap';
import { usePage, router } from '@inertiajs/react';

import Valuation from '../../Data/Valuation';
import PlCategory from '../../Data/PlCategory';
import { PlStatementUpload, ReviewLineItem, ExistingDataByColumn, MergeStrategy } from '../../Data/PlStatementUpload';
import { PeriodLabels, PeriodLabelText } from '../../Data/PeriodLabels';
import PeriodColumnHeader from '../../Components/PeriodColumnHeader';
import AuthedLayout from '../../Layouts/Authed';
import FormHeader from '../../Components/Forms/FormHeader';

interface Props {
    valuation: Valuation;
    upload: PlStatementUpload;
    categories: PlCategory[];
    existingDataByColumn: ExistingDataByColumn;
    periodLabels?: PeriodLabels;
}

const COLUMN_KEYS = ['two_years_ago', 'one_year_ago', 'current'] as const;

const COLUMN_LABEL_FALLBACKS: Record<typeof COLUMN_KEYS[number], PeriodLabelText> = {
    current: PeriodLabelText.Current,
    one_year_ago: PeriodLabelText.OneYearAgo,
    two_years_ago: PeriodLabelText.TwoYearsAgo,
};

const PlStatementReview = (props: Props) => {
    const { errors } = usePage().props;
    const aiResponse = props.upload.ai_response;

    // Determine which columns this upload has data in
    const uploadHasDataInColumn = (col: typeof COLUMN_KEYS[number]): boolean => {
        const allItems = [
            ...(aiResponse?.line_items || []),
            ...(aiResponse?.unmapped_items || []),
        ];
        return allItems.some((item) => item.values[col] != null);
    };

    // Initialize per-column strategies for columns that have conflicts
    const [columnStrategies, setColumnStrategies] = useState<Partial<Record<typeof COLUMN_KEYS[number], MergeStrategy>>>(() => {
        const strategies: Partial<Record<typeof COLUMN_KEYS[number], MergeStrategy>> = {};
        COLUMN_KEYS.forEach((col) => {
            if (props.existingDataByColumn[col] && uploadHasDataInColumn(col)) {
                strategies[col] = 'fill_gaps';
            }
        });
        return strategies;
    });

    const hasConflicts = Object.keys(columnStrategies).length > 0;

    // Use previously approved mappings if available, otherwise initialize from AI response
    const getInitialItems = (): ReviewLineItem[] => {
        if (props.upload.approved_mappings && props.upload.approved_mappings.length > 0) {
            return props.upload.approved_mappings;
        }

        const mappedItems: ReviewLineItem[] = (aiResponse?.line_items || []).map((item) => ({
            ...item,
            include: item.confidence >= 0.7,
            subcategory_id: item.suggested_subcategory_id,
            isUnmapped: false,
        }));

        const unmappedItems: ReviewLineItem[] = (aiResponse?.unmapped_items || []).map((item) => ({
            original_text: item.original_text,
            values: item.values,
            suggested_subcategory_id: null,
            suggested_subcategory_name: '',
            confidence: 0,
            reasoning: item.reasoning,
            include: false,
            subcategory_id: null,
            isUnmapped: true,
            suggested_category_type: item.suggested_category_type,
        }));

        return [...mappedItems, ...unmappedItems];
    };

    const [items, setItems] = useState<ReviewLineItem[]>(getInitialItems);
    const [processing, setProcessing] = useState(false);
    const [feedbackText, setFeedbackText] = useState('');
    const [feedbackScope, setFeedbackScope] = useState<'this_upload' | 'source_system'>('this_upload');
    const [reprocessing, setReprocessing] = useState(false);

    const submitFeedback = (event: React.FormEvent<any>) => {
        event.preventDefault();
        if (!feedbackText.trim()) return;
        setReprocessing(true);
        router.post(
            `/valuation-form/${props.valuation.id}/pl-statement-review/${props.upload.id}/reprocess`,
            { text: feedbackText, scope: feedbackScope } as any,
            { onFinish: () => setReprocessing(false) },
        );
    };

    const updateItem = (index: number, field: string, value: any) => {
        const newItems = [...items];
        if (field.startsWith('values.')) {
            const valueField = field.split('.')[1];
            newItems[index] = {
                ...newItems[index],
                values: {
                    ...newItems[index].values,
                    [valueField]: value === '' ? null : parseFloat(value),
                },
            };
        } else {
            newItems[index] = { ...newItems[index], [field]: value };
        }
        setItems(newItems);
    };

    const submit = (event: React.FormEvent<any>) => {
        event.preventDefault();
        setProcessing(true);
        router.post(
            `/valuation-form/${props.valuation.id}/pl-statement-review/${props.upload.id}`,
            { items, column_strategies: columnStrategies } as any,
            { onFinish: () => setProcessing(false) },
        );
    };

    const getConfidenceBadge = (confidence: number) => {
        if (confidence >= 0.9) return <Badge bg="success">{Math.round(confidence * 100)}%</Badge>;
        if (confidence >= 0.7) return <Badge bg="warning" className="text-black">{Math.round(confidence * 100)}%</Badge>;
        return <Badge bg="danger">{Math.round(confidence * 100)}%</Badge>;
    };

    const getAllSubcategories = () => {
        const subcategories: { id: number; name: string; categoryName: string }[] = [];
        props.categories.forEach((cat) => {
            cat.pl_subcategories?.forEach((sub) => {
                subcategories.push({
                    id: sub.id,
                    name: sub.name,
                    categoryName: cat.name,
                });
            });
        });
        return subcategories;
    };

    const allSubcategories = getAllSubcategories();

    const selectAll = () => {
        setItems(items.map((item: ReviewLineItem) => ({ ...item, include: true })));
    };

    const selectNone = () => {
        setItems(items.map((item: ReviewLineItem) => ({ ...item, include: false })));
    };

    const selectHighConfidence = () => {
        setItems(items.map((item: ReviewLineItem) => ({
            ...item,
            include: item.confidence >= 0.9,
        })));
    };

    const getColumnLabel = (col: typeof COLUMN_KEYS[number]): string => {
        if (!props.periodLabels) {
            return COLUMN_LABEL_FALLBACKS[col];
        }
        const pl = props.periodLabels[col];
        return `${pl.label} (${pl.range})`;
    };

    if (!aiResponse || Array.isArray(aiResponse) || !aiResponse.line_items) {
        return (
            <div>
                <FormHeader
                    agencyName={props.valuation.agency_name}
                    effectiveDate={props.valuation.effective_date}
                    pageName="Review P&L Import"
                />
                <Alert variant="danger">
                    No parsed data available for this upload. The AI may have failed to parse the document.
                    <br />
                    <a href={`/valuation-form/${props.valuation.id}/pl-statement-upload`}>
                        Go back and try uploading again
                    </a>
                </Alert>
            </div>
        );
    }

    return (
        <div>
            <FormHeader
                agencyName={props.valuation.agency_name}
                effectiveDate={props.valuation.effective_date}
                pageName="Review P&L Import"
            />

            <Card className="mb-4">
                <Card.Header>
                    File: <strong>{props.upload.original_filename}</strong>
                </Card.Header>
                {aiResponse.detected_years && Object.keys(aiResponse.detected_years).length > 0 && (
                    <Card.Body className="py-2">
                        <small className="text-muted fw-semibold d-block mb-1">Detected periods (mapped by AI):</small>
                        <div className="d-flex flex-wrap gap-3">
                            {COLUMN_KEYS.filter((col) => aiResponse.detected_years[col]).map((col) => (
                                <span key={col} className="d-flex align-items-center gap-1">
                                    <Badge style={{
                                        backgroundColor: col === 'current' ? '#0d6efd' : col === 'one_year_ago' ? '#6c757d' : '#495057',
                                        color: '#fff',
                                    }}>
                                        {props.periodLabels?.[col].label ?? col.replace(/_/g, ' ')}
                                    </Badge>
                                    <small>{aiResponse.detected_years[col]}</small>
                                </span>
                            ))}
                        </div>
                    </Card.Body>
                )}
            </Card>

            <Accordion className="mb-4" defaultActiveKey={props.upload.feedback && props.upload.feedback.length > 0 ? '0' : undefined}>
                <Accordion.Item eventKey="0">
                    <Accordion.Header>
                        <strong>Reprocess with Feedback</strong>
                    </Accordion.Header>
                    <Accordion.Body>
                        <p className="text-muted small mb-3">
                            If the AI pulled the wrong data (e.g. wrong column, wrong period), add feedback below and reprocess.
                            The same document will be re-analyzed with your guidance included.
                        </p>
                        <div className="text-muted small mb-3">
                            <strong>Tips for effective feedback:</strong>
                            <ul className="mb-0 mt-1">
                                <li>Be specific about which column maps to which year. Example: <em>"Take the rightmost column and map it to current"</em>.</li>
                                <li>Valid year slots are <code>current</code>, <code>one year ago</code>, and <code>two years ago</code>.</li>
                                <li>Anchoring to a concrete value can help. Example: <em>"The column starting with $156.30 should map to current."</em></li>
                                <li>Keep instructions short and direct.</li>
                                <li>If a reprocess didn't follow your instructions, try again. All previous feedback is included on each attempt.</li>
                            </ul>
                        </div>
                        {props.upload.feedback && props.upload.feedback.length > 0 && (
                            <div className="mb-3">
                                <small className="fw-semibold text-muted">Previous feedback:</small>
                                <ul className="small mb-0 mt-1">
                                    {props.upload.feedback.map((entry, i) => (
                                        <li key={i}>
                                            <em>"{entry.text}"</em>
                                            <Badge bg="secondary" className="ms-2" style={{ fontSize: '0.7rem' }}>
                                                {entry.scope === 'source_system' ? `${props.upload.source_system ?? 'source'}` : 'this document'}
                                            </Badge>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <Form onSubmit={submitFeedback}>
                            <Form.Group className="mb-3">
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="e.g. Ignore the dates at the top of these columns. Use the second column from the right and map it to current."
                                    value={feedbackText}
                                    onChange={(e) => setFeedbackText(e.target.value)}
                                />
                            </Form.Group>
                            <div className="d-flex align-items-center gap-3 flex-wrap">
                                <Form.Check
                                    type="radio"
                                    id="scope-this"
                                    label="For this document only"
                                    checked={feedbackScope === 'this_upload'}
                                    onChange={() => setFeedbackScope('this_upload')}
                                />
                                <Form.Check
                                    type="radio"
                                    id="scope-source"
                                    label={`For all ${props.upload.source_system ?? 'uploads with this source'}`}
                                    checked={feedbackScope === 'source_system'}
                                    onChange={() => setFeedbackScope('source_system')}
                                    disabled={!props.upload.source_system}
                                />
                                <Button
                                    type="submit"
                                    variant="warning"
                                    className="ms-auto"
                                    disabled={reprocessing || !feedbackText.trim()}
                                >
                                    {reprocessing ? 'Reprocessing...' : 'Reprocess with Feedback'}
                                </Button>
                            </div>
                        </Form>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            {aiResponse.warnings && aiResponse.warnings.length > 0 && (
                <Alert variant="warning" className="mb-4">
                    <strong>Warnings:</strong>
                    <ul className="mb-0 mt-2">
                        {aiResponse.warnings.map((warning, i) => (
                            <li key={i}>{warning}</li>
                        ))}
                    </ul>
                </Alert>
            )}

            {hasConflicts && (
                <Alert variant="info" className="mb-4">
                    <strong>This valuation already has data for the following periods:</strong>
                    {Object.entries(columnStrategies).map(([col, strategy]) => (
                        <div key={col} className="d-flex align-items-center mt-2">
                            <span className="me-3 fw-semibold" style={{ minWidth: '220px' }}>
                                {getColumnLabel(col as typeof COLUMN_KEYS[number])}
                            </span>
                            <Form.Select
                                size="sm"
                                style={{ width: '200px' }}
                                value={strategy}
                                onChange={(e) => setColumnStrategies({
                                    ...columnStrategies,
                                    [col]: e.target.value as MergeStrategy,
                                })}
                            >
                                <option value="fill_gaps">Fill Gaps</option>
                                <option value="replace">Replace</option>
                                <option value="skip">Skip</option>
                            </Form.Select>
                        </div>
                    ))}
                    <div className="mt-3">
                        <small className="text-muted">
                            <strong>Fill Gaps</strong> - Only write values where the existing record is empty &bull;{' '}
                            <strong>Replace</strong> - Overwrite existing values &bull;{' '}
                            <strong>Skip</strong> - Keep existing data, ignore this upload's values
                        </small>
                    </div>
                </Alert>
            )}

            {errors.general && (
                <Alert variant="danger" className="mb-4">{errors.general}</Alert>
            )}

            <Form onSubmit={submit}>
                <Card className="mb-4">
                    <Card.Header>
                        <div className="d-flex justify-content-between align-items-center">
                            <span>Mapped Line Items ({items.filter((item) => !item.isUnmapped).length})</span>
                            <div>
                                <Button variant="outline-secondary" size="sm" onClick={selectAll} className="me-2">
                                    Select All
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={selectHighConfidence} className="me-2">
                                    Select High Confidence
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={selectNone}>
                                    Select None
                                </Button>
                            </div>
                        </div>
                    </Card.Header>
                    <Card.Body>
                        <Table striped bordered hover responsive size="sm">
                            <thead>
                                <tr>
                                    <th style={{ width: '40px' }}>Include</th>
                                    <th>Original Text</th>
                                    <th>Map To</th>
                                    <th style={{ width: '60px' }}>Conf.</th>
                                    <th style={{ width: '120px' }}><PeriodColumnHeader periodLabel={props.periodLabels?.two_years_ago} fallback={PeriodLabelText.TwoYearsAgo} /></th>
                                    <th style={{ width: '120px' }}><PeriodColumnHeader periodLabel={props.periodLabels?.one_year_ago} fallback={PeriodLabelText.OneYearAgo} /></th>
                                    <th style={{ width: '120px' }}><PeriodColumnHeader periodLabel={props.periodLabels?.current} fallback={PeriodLabelText.Current} /></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, index) => {
                                    if (item.isUnmapped) return null;
                                    return (
                                        <tr key={index} className={!item.include ? 'table-secondary' : ''}>
                                            <td className="text-center">
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={item.include}
                                                    onChange={(e) => updateItem(index, 'include', e.target.checked)}
                                                />
                                            </td>
                                            <td>
                                                <small>{item.original_text}</small>
                                                <br />
                                                <small className="text-muted">{item.reasoning}</small>
                                            </td>
                                            <td>
                                                <Form.Select
                                                    size="sm"
                                                    value={item.subcategory_id || ''}
                                                    onChange={(e) => {
                                                        const newId = e.target.value ? parseInt(e.target.value) : null;
                                                        updateItem(index, 'subcategory_id', newId);
                                                    }}
                                                >
                                                    <option value="">-- Select --</option>
                                                    {allSubcategories.map((sub) => (
                                                        <option key={sub.id} value={sub.id}>
                                                            {sub.categoryName} &gt; {sub.name}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                {item.analyst_note != null ? (
                                                    <div className="mt-1">
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={1}
                                                            size="sm"
                                                            placeholder="Note..."
                                                            value={item.analyst_note || ''}
                                                            onChange={(e) => updateItem(index, 'analyst_note', e.target.value)}
                                                        />
                                                        <div className="text-end">
                                                            <a
                                                                href="#"
                                                                className="text-muted"
                                                                style={{ fontSize: '0.75rem' }}
                                                                onClick={(e) => { e.preventDefault(); updateItem(index, 'analyst_note', null); }}
                                                            >
                                                                remove note
                                                            </a>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-end">
                                                        <a
                                                            href="#"
                                                            className="text-muted"
                                                            style={{ fontSize: '0.75rem' }}
                                                            onClick={(e) => { e.preventDefault(); updateItem(index, 'analyst_note', ''); }}
                                                        >
                                                            + add note
                                                        </a>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                {getConfidenceBadge(item.confidence)}
                                            </td>
                                            <td>
                                                <Form.Control
                                                    type="number"
                                                    size="sm"
                                                    step="0.01"
                                                    value={item.values.two_years_ago ?? ''}
                                                    onChange={(e) => updateItem(index, 'values.two_years_ago', e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <Form.Control
                                                    type="number"
                                                    size="sm"
                                                    step="0.01"
                                                    value={item.values.one_year_ago ?? ''}
                                                    onChange={(e) => updateItem(index, 'values.one_year_ago', e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <Form.Control
                                                    type="number"
                                                    size="sm"
                                                    step="0.01"
                                                    value={item.values.current ?? ''}
                                                    onChange={(e) => updateItem(index, 'values.current', e.target.value)}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>

                {items.some((item) => item.isUnmapped) && (
                    <Card className="mb-4">
                        <Card.Header className="bg-warning">
                            Unmapped Items ({items.filter((item) => item.isUnmapped).length})
                        </Card.Header>
                        <Card.Body>
                            <p className="text-muted">
                                These items could not be automatically mapped. Select a category to include them.
                            </p>
                            <Table striped bordered hover responsive size="sm">
                                <thead>
                                    <tr>
                                        <th style={{ width: '40px' }}>Include</th>
                                        <th>Original Text</th>
                                        <th>Map To</th>
                                        <th style={{ width: '100px' }}>Suggested Type</th>
                                        <th style={{ width: '120px' }}>{props.periodLabels?.two_years_ago.label ?? '2 Yrs Ago'}<br/><small className="text-muted fw-normal">{props.periodLabels?.two_years_ago.range}</small></th>
                                        <th style={{ width: '120px' }}>{props.periodLabels?.one_year_ago.label ?? '1 Yr Ago'}<br/><small className="text-muted fw-normal">{props.periodLabels?.one_year_ago.range}</small></th>
                                        <th style={{ width: '120px' }}>{props.periodLabels?.current.label ?? 'Current'}<br/><small className="text-muted fw-normal">{props.periodLabels?.current.range}</small></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item, index) => {
                                        if (!item.isUnmapped) return null;
                                        return (
                                            <tr key={index}>
                                                <td className="text-center">
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={item.include}
                                                        onChange={(e) => updateItem(index, 'include', e.target.checked)}
                                                    />
                                                </td>
                                                <td>
                                                    <small>{item.original_text}</small>
                                                    <br />
                                                    <small className="text-muted">{item.reasoning}</small>
                                                </td>
                                                <td>
                                                    <Form.Select
                                                        size="sm"
                                                        value={item.subcategory_id || ''}
                                                        onChange={(e) => {
                                                            const newId = e.target.value ? parseInt(e.target.value) : null;
                                                            updateItem(index, 'subcategory_id', newId);
                                                        }}
                                                    >
                                                        <option value="">-- Select --</option>
                                                        {allSubcategories.map((sub) => (
                                                            <option key={sub.id} value={sub.id}>
                                                                {sub.categoryName} &gt; {sub.name}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                    {item.analyst_note != null ? (
                                                        <div className="mt-1">
                                                            <Form.Control
                                                                as="textarea"
                                                                rows={1}
                                                                size="sm"
                                                                placeholder="Note..."
                                                                value={item.analyst_note || ''}
                                                                onChange={(e) => updateItem(index, 'analyst_note', e.target.value)}
                                                            />
                                                            <a
                                                                href="#"
                                                                className="text-muted"
                                                                style={{ fontSize: '0.75rem' }}
                                                                onClick={(e) => { e.preventDefault(); updateItem(index, 'analyst_note', null); }}
                                                            >
                                                                remove note
                                                            </a>
                                                        </div>
                                                    ) : (
                                                        <div>
                                                            <a
                                                                href="#"
                                                                className="text-muted"
                                                                style={{ fontSize: '0.75rem' }}
                                                                onClick={(e) => { e.preventDefault(); updateItem(index, 'analyst_note', ''); }}
                                                            >
                                                                + add note
                                                            </a>
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <small>{item.suggested_category_type}</small>
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="number"
                                                        size="sm"
                                                        step="0.01"
                                                        value={item.values.two_years_ago ?? ''}
                                                        onChange={(e) => updateItem(index, 'values.two_years_ago', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="number"
                                                        size="sm"
                                                        step="0.01"
                                                        value={item.values.one_year_ago ?? ''}
                                                        onChange={(e) => updateItem(index, 'values.one_year_ago', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="number"
                                                        size="sm"
                                                        step="0.01"
                                                        value={item.values.current ?? ''}
                                                        onChange={(e) => updateItem(index, 'values.current', e.target.value)}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                )}

                <div className="d-flex justify-content-between">
                    <a
                        href={`/valuation-form/${props.valuation.id}/pl-statement-upload`}
                        className="btn btn-outline-secondary"
                    >
                        Back to Upload
                    </a>
                    <Button type="submit" disabled={processing}>
                        {processing ? 'Applying...' : 'Apply Selected Items'}
                    </Button>
                </div>
            </Form>
        </div>
    );
};

PlStatementReview.layout = (page: ReactNode) => <AuthedLayout>{page}</AuthedLayout>;

export default PlStatementReview;
